import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, X, Send, Users, Mail, Phone, MessageCircle, RefreshCcw, Calendar, Clock } from 'lucide-react';
import { toast } from './ToastProvider';
import ReactConfetti from 'react-confetti';

const RSVPSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [rsvpList, setRsvpList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    numberOfGuests: 1,
    message: '',
    attending: 'yes'
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const fetchRSVPList = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://api.quanhd.net/api/rsvp');
      const data = await response.json();
      setRsvpList(data);
    } catch (error) {
      console.error('Error fetching RSVP list:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (showAdmin) {
      fetchRSVPList();
    }
  }, [showAdmin]);

  // Validation functions
  const isValidPhone = (phone) => {
    const phoneRegex = /(84|0[3|5|7|8|9])+([0-9]{8})\b/;
    return phoneRegex.test(phone);
  };

  const isValidEmail = (email) => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Validate before submitting
    if (!isValidPhone(formData.phone)) {
      toast.error('Số điện thoại không hợp lệ. Vui lòng nhập số điện thoại có 10 số.');
      setSubmitting(false);
      return;
    }

    if (formData.email && !isValidEmail(formData.email)) {
      toast.error('Email không hợp lệ. Vui lòng kiểm tra lại.');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://api.quanhd.net/api/rsvp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          phone: formData.phone.replace(/\s/g, ''), // Remove spaces from phone
          timestamp: new Date().toISOString()
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        if (data.error === 'Invalid data' && data.messages) {
          const errorMessage = Object.values(data.messages)
            .flat()
            .join('\n');
          toast.error(errorMessage);
          return;
        }
        if (data.error === 'Duplicate data' && data.message) {
          toast.error(data.message);
          return;
        }
        toast.error(data.message || 'Có lỗi xảy ra khi gửi form. Vui lòng thử lại sau.');
        return;
      }

      toast.success(data.message || 'Đăng ký thành công!');
      setSubmitted(true);
      setIsModalOpen(false);
      setFormData({
        name: '',
        phone: '',
        email: '',
        numberOfGuests: 1,
        message: '',
        attending: 'yes'
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Có lỗi xảy ra khi gửi form. Vui lòng thử lại sau.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const RSVPCard = React.memo(({ rsvp }) => {
    const date = new Date(rsvp.timestamp);

    // Hàm cắt message xuống còn tối đa 100 ký tự
    const truncateMessage = (message, maxLength = 100) => {
      if (!message) return '';
      if (message.length <= maxLength) return message;
      return message.slice(0, maxLength) + '...';
    };

    return (
      <div
        className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-shadow"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-gray-800">{rsvp.name}</h4>
            <div className="flex items-center gap-2 text-gray-500 text-sm mt-1">
              <Calendar className="w-4 h-4" />
              <span>{date.toLocaleDateString()}</span>
              <Clock className="w-4 h-4 ml-2" />
              <span>{date.toLocaleTimeString()}</span>
            </div>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-sm ${rsvp.attending === 'yes'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
              }`}
          >
            {rsvp.attending === 'yes' ? 'Tham dự' : 'Không tham dự'}
          </span>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-gray-600">
            <Phone className="w-4 h-4" />
            <span>{rsvp.phone}</span>
          </div>
          {rsvp.email && (
            <div className="flex items-center gap-2 text-gray-600">
              <Mail className="w-4 h-4" />
              <span>{rsvp.email}</span>
            </div>
          )}
          {rsvp.attending === 'yes' && (
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="w-4 h-4" />
              <span>{rsvp.numberOfGuests} người</span>
            </div>
          )}
        </div>

        {rsvp.message && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-start gap-2">
              <MessageCircle className="w-4 h-4 text-gray-400 mt-1" />
              <p className="text-gray-600 italic">
                {truncateMessage(rsvp.message)}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  });

  const AdminPanel = () => {
    const totalGuests = rsvpList
      .filter(rsvp => rsvp.attending === 'yes')
      .reduce((sum, rsvp) => sum + parseInt(rsvp.numberOfGuests), 0);

    const attendingCount = rsvpList.filter(rsvp => rsvp.attending === 'yes').length;
    const notAttendingCount = rsvpList.filter(rsvp => rsvp.attending === 'no').length;

    return (
      <div className="mt-12 max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
              <h3 className="text-2xl text-gray-800 mb-2" style={{ fontFamily: '"Playfair Display", serif' }}>
                Danh sách đăng ký tham dự
              </h3>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="bg-pink-50 text-pink-700 px-4 py-2 rounded-full">
                  Tổng số: {rsvpList.length} đăng ký
                </div>
                <div className="bg-green-50 text-green-700 px-4 py-2 rounded-full">
                  Tham dự: {attendingCount} người
                </div>
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded-full">
                  Không tham dự: {notAttendingCount} người
                </div>
                <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-full">
                  Tổng số khách: {totalGuests} người
                </div>
              </div>
            </div>
            <button
              onClick={fetchRSVPList}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors flex items-center gap-2"
              disabled={loading}
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Làm mới</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rsvpList.map((rsvp, index) => (
              <RSVPCard key={index} rsvp={rsvp} />
            ))}
          </div>

          {rsvpList.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">
              Chưa có đăng ký nào
            </div>
          )}

          {loading && (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-t-2 border-pink-500 border-solid rounded-full animate-spin mx-auto"></div>
              <p className="text-gray-500 mt-4">Đang tải dữ liệu...</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="py-24 px-4 bg-gradient-to-b from-pink-50 to-white">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-7xl mx-auto text-center w-full"
      >
        <div className="relative mb-8">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="border-t-2 border-pink-200 w-full"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-gradient-to-b from-pink-50 to-white px-4">
              <Heart className="text-pink-400 w-8 h-8 animate-pulse" />
            </span>
          </div>
        </div>

        <h2 className="text-4xl md:text-5xl mb-6 text-gray-800 font-greatVibes">
          Xác nhận tham dự
        </h2>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-white p-8 md:p-12 rounded-3xl shadow-2xl border border-pink-100 backdrop-blur-sm bg-white/90 max-w-3xl mx-auto"
        >
          <p className="text-gray-600 mb-8 text-lg" style={{ fontFamily: '"Dancing Script", cursive' }}>
            Hân hạnh được đón tiếp bạn trong ngày vui của chúng tôi
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-pink-500 text-white px-10 py-4 rounded-full text-lg hover:bg-pink-600 transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 flex items-center justify-center mx-auto gap-2"
          >
            <Send className="w-5 h-5" />
            <span style={{ fontFamily: '"Montserrat", sans-serif' }}>Xác nhận ngay</span>
          </button>
        </motion.div>

        <div className="mt-8 text-center">
          <button
            onClick={() => setShowAdmin(!showAdmin)}
            className="text-gray-500 hover:text-gray-700 text-sm underline"
          >
            {showAdmin ? 'Ẩn danh sách' : 'Xem danh sách đăng ký'}
          </button>
        </div>
      </motion.div>

      {showAdmin && <AdminPanel />}

      <AnimatePresence>
        {submitted && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <ReactConfetti
              width={window.innerWidth}
              height={window.innerHeight}
              recycle={false}
              numberOfPieces={500}
              tweenDuration={10000}
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#FDF6F6] rounded-3xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
            >
              <div className="relative p-6 sm:p-8 text-center">
                {/* Decorative elements */}
                <div className="absolute top-0 left-0 w-24 h-24 bg-pink-100 opacity-50 rounded-full -translate-x-12 -translate-y-12" />
                <div className="absolute top-0 right-0 w-24 h-24 bg-pink-100 opacity-50 rounded-full translate-x-12 -translate-y-12" />

                {/* Content */}
                <div className="relative z-10">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 260,
                      damping: 20,
                      delay: 0.3
                    }}
                    className="w-24 h-24 bg-pink-50 rounded-full mx-auto mb-6 flex items-center justify-center"
                  >
                    <Heart className="text-pink-500 w-12 h-12 animate-pulse" />
                  </motion.div>

                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <h3 className="text-2xl sm:text-3xl text-gray-800 mb-4 font-greatVibes">
                      Cảm ơn bạn!
                    </h3>

                    <p className="text-gray-600 mb-6 font-dancing text-lg">
                      Cảm ơn bạn đã xác nhận tham dự. Chúng tôi rất vui mừng và mong được gặp bạn trong ngày vui của chúng tôi.
                    </p>

                    <div className="flex justify-center space-x-2 text-sm">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-6 py-2 bg-pink-500 text-white rounded-full hover:bg-pink-600 transition-colors"
                        onClick={() => {
                          setIsModalOpen(false);
                          setSubmitted(false);
                          setFormData({
                            name: '',
                            phone: '',
                            email: '',
                            numberOfGuests: 1,
                            message: '',
                            attending: 'yes'
                          });
                        }}
                      >
                        Đóng
                      </motion.button>
                    </div>
                  </motion.div>
                </div>

                {/* Bottom decorative element */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-32 h-32 bg-pink-100 opacity-50 rounded-full" />
              </div>
            </motion.div>
          </motion.div>
        )}
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsModalOpen(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-lg relative my-8 mx-auto"
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="p-4 sm:p-6 md:p-8">
                <h3 className="text-xl sm:text-2xl text-gray-800 mb-6 text-center" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Thông tin tham dự
                </h3>

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div className="space-y-2">
                    <label className="block text-gray-700 text-sm font-medium" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                      Bạn có thể tham dự không? *
                    </label>
                    <div className="flex gap-6">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="attending"
                          value="yes"
                          checked={formData.attending === 'yes'}
                          onChange={handleChange}
                          className="w-4 h-4 text-pink-500 mr-2"
                        />
                        <span className="text-sm" style={{ fontFamily: '"Montserrat", sans-serif' }}>Có</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="attending"
                          value="no"
                          checked={formData.attending === 'no'}
                          onChange={handleChange}
                          className="w-4 h-4 text-pink-500 mr-2"
                        />
                        <span className="text-sm" style={{ fontFamily: '"Montserrat", sans-serif' }}>Không</span>
                      </label>
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-gray-700 text-sm font-medium mb-1" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                      Họ và tên *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300"
                      style={{ fontFamily: '"Montserrat", sans-serif' }}
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-gray-700 text-sm font-medium mb-1" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                      Số điện thoại *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-gray-700 text-sm font-medium mb-1" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      />
                    </div>
                  </div>

                  {formData.attending === 'yes' && (
                    <div className="relative">
                      <label className="block text-gray-700 text-sm font-medium mb-1" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                        Số lượng người đi cùng
                      </label>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="number"
                          name="numberOfGuests"
                          value={formData.numberOfGuests}
                          onChange={handleChange}
                          min="1"
                          max="10"
                          className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300"
                          style={{ fontFamily: '"Montserrat", sans-serif' }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="relative">
                    <label className="block text-gray-700 text-sm font-medium mb-1" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                      Lời nhắn
                    </label>
                    <div className="relative">
                      <MessageCircle className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="3"
                        className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      ></textarea>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className={`w-full bg-pink-500 text-white py-3 rounded-xl text-sm sm:text-base
                          ${submitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-pink-600 hover:shadow-lg transform hover:-translate-y-1'} 
                          transition-all duration-300 flex items-center justify-center gap-2`}
                    style={{ fontFamily: '"Montserrat", sans-serif' }}
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-t-2 border-white rounded-full animate-spin"></div>
                        <span>Đang gửi...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Gửi xác nhận</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default RSVPSection;